/**
 * Permissions Context
 * Manages user permissions and navigation access for CampusOS
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PermissionsContext = createContext(null);

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionsProvider');
  }
  return context;
};

export const PermissionsProvider = ({ children }) => {
  const { session, user, organization } = useAuth();
  
  const [role, setRole] = useState(null);
  const [officerTitle, setOfficerTitle] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [navigation, setNavigation] = useState({ sections: [] });
  const [isDoorStaffOnly, setIsDoorStaffOnly] = useState(false);
  const [doorStaffEvent, setDoorStaffEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user's permissions from the backend
  const fetchPermissions = useCallback(async () => {
    if (!session?.access_token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch permissions
      const permsRes = await fetch(`${API_URL}/api/permissions/my-permissions`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });

      if (!permsRes.ok) {
        throw new Error('Failed to fetch permissions');
      }

      const permsData = await permsRes.json();
      setRole(permsData.role);
      setOfficerTitle(permsData.officer_title);
      setPermissions(permsData.permissions || []);
      setIsDoorStaffOnly(permsData.is_door_staff_only || false);
      setDoorStaffEvent(permsData.door_staff_event);

      // Fetch navigation
      const navRes = await fetch(`${API_URL}/api/permissions/navigation`, {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });

      if (navRes.ok) {
        const navData = await navRes.json();
        setNavigation(navData);
      }

    } catch (err) {
      console.error('Failed to fetch permissions:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [session?.access_token]);

  // Refetch when session or organization changes
  useEffect(() => {
    fetchPermissions();
  }, [fetchPermissions, organization?.id]);

  // Check if user has a specific permission
  const hasPermission = useCallback((permissionKey) => {
    if (!role) return false;
    
    // Owner has all permissions
    if (role === 'owner') return true;
    
    return permissions.includes(permissionKey);
  }, [role, permissions]);

  // Check if user has any of the specified roles
  const hasRole = useCallback((roles) => {
    if (!role) return false;
    if (typeof roles === 'string') return role === roles;
    return roles.includes(role);
  }, [role]);

  // Check if user can access a specific path
  const canAccessPath = useCallback((path) => {
    if (!role) return false;
    
    // Owner can access everything
    if (role === 'owner') return true;

    // Map paths to permission keys
    const pathToPermission = {
      '/app/events': 'events',
      '/app/events/new': 'create_event',
      '/app/attendees': 'attendee_lists',
      '/app/scanner': 'qr_scanner',
      '/app/payments': 'payments',
      '/app/age-verification': 'age_verification',
      '/app/analytics': 'analytics',
      '/app/members': 'members',
      '/app/recruitment': 'recruitment',
      '/app/dues': 'dues',
      '/app/documents': 'documents',
      '/app/announcements': 'announcements',
      '/app/alumni': 'alumni_directory',
      '/app/sponsors': 'sponsors_wallet',
      '/app/dashboard': 'dashboard',
      '/app/my-events': 'my_events',
      '/app/my-ticket': 'my_ticket',
      '/app/browse': 'browse_events',
      '/app/my-dues': 'my_dues',
      '/app/profile': 'my_profile',
    };

    // Find matching permission
    const permKey = Object.entries(pathToPermission).find(([p]) => 
      path.startsWith(p)
    )?.[1];

    if (!permKey) {
      // Unknown path - allow by default for flexibility
      return true;
    }

    return permissions.includes(permKey);
  }, [role, permissions]);

  // Get role display name
  const getRoleDisplayName = useCallback(() => {
    if (!role) return '';
    
    const roleNames = {
      owner: 'Chapter President',
      admin: officerTitle || 'Officer',
      event_manager: 'Events Officer',
      door_staff: 'Door Staff',
      member: 'Member'
    };
    
    return roleNames[role] || role;
  }, [role, officerTitle]);

  const value = {
    // State
    role,
    officerTitle,
    permissions,
    navigation,
    isDoorStaffOnly,
    doorStaffEvent,
    loading,
    error,
    
    // Methods
    hasPermission,
    hasRole,
    canAccessPath,
    getRoleDisplayName,
    refreshPermissions: fetchPermissions,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

export default PermissionsContext;
